import type {
	AssetSheetProgress,
	AssetSheetRowReportDTO,
	AssetSheetSyncCompletedData,
	AssetSheetTabProblem,
	PaginatedResponse,
} from '@atlas/types'
import {
	AssetSheetDerivedRowStatus,
	EventEntityType,
	EventType,
	VeevaVaultAssetImportStatus,
	VeevaVaultFileImportStatus,
} from '@atlas/types'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { activityApi, type EventDTO } from '@/services/activity.service'
import { type AssetSheetRowFilters, assetSheetApi } from '@/services/asset-sheet.service'

const ACTIVE_REFETCH_MS = 5_000
const IDLE_REFETCH_MS = 30_000
const ACTIVITY_LIMIT = 10
/** Fast polling ends once the counts have not moved for this long: work stuck for good moves nothing. */
const STILL_AFTER_MS = 5 * 60_000

export const assetSheetKey = ['asset-sheet'] as const
const progressKey = [...assetSheetKey, 'progress'] as const

/** Whether any of the three processes still has work in flight; stalled files wait for a sync, not for time. */
export const isActive = ({ sync, downloads, tagging }: AssetSheetProgress) =>
	sync.running ||
	downloads.inProgress > 0 ||
	downloads.notStarted > 0 ||
	tagging.tagged + tagging.stalled < tagging.files ||
	tagging.toRemove > 0

/**
 * Where each process stands, polled fast while any of them is working and its counts keep
 * moving, or while `isExpecting` says a sync was just asked for and has not taken its lock yet.
 * A first read that failed is not polled: the page offers to try again instead.
 */
export const useAssetSheetProgress = (isExpecting = false) => {
	// Structural sharing keeps the data object while its content is unchanged, so a new one means movement.
	const moved = useRef<{ data?: AssetSheetProgress; at: number }>({ at: Date.now() })
	return useQuery({
		queryKey: progressKey,
		queryFn: assetSheetApi.getProgress,
		refetchInterval: (q) => {
			const data = q.state.data
			if (data && data !== moved.current.data) moved.current = { data, at: Date.now() }
			return progressPollMs(data, q.state.error !== null, isExpecting, Date.now() - moved.current.at)
		},
	})
}

/**
 * How soon to read the progress again: fast while work is in flight and its counts moved in the
 * last few minutes, or a sync was just asked for; never after a first read failed.
 */
export function progressPollMs(
	data: AssetSheetProgress | undefined,
	failed: boolean,
	isExpecting: boolean,
	stillForMs: number,
): number | false {
	if (!data) return failed ? false : IDLE_REFETCH_MS
	return isExpecting || (isActive(data) && stillForMs < STILL_AFTER_MS) ? ACTIVE_REFETCH_MS : IDLE_REFETCH_MS
}

const rowsKey = [...assetSheetKey, 'rows'] as const

/** One page of the rows matching the Rows tab's filters; the previous page stays on screen while the next loads. */
export const useAssetSheetRows = (filters: AssetSheetRowFilters) =>
	useQuery({
		queryKey: [...rowsKey, filters],
		queryFn: () => assetSheetApi.listRows(filters),
		placeholderData: keepPreviousData,
	})

/** Reads the rows again once a sync has finished, since they are not polled. */
export const useRefreshRowsAfterSync = (lastSyncedAt: string | null) => {
	const queryClient = useQueryClient()
	useEffect(() => {
		if (lastSyncedAt) void queryClient.invalidateQueries({ queryKey: rowsKey })
	}, [lastSyncedAt, queryClient])
}

const SYNC_OUTCOMES = [EventType.ASSET_SHEET_SYNC_COMPLETED, EventType.ASSET_SHEET_SYNC_FAILED]

/** The events Recent activity describes: older asset sheet events, such as a workbook upload, have no wording left. */
const ACTIVITY_TYPES = [...SYNC_OUTCOMES, EventType.ASSET_SHEET_COLUMN_MAP_UPDATED]

/** The latest sync outcomes and column map changes, from the audit log; polled fast while `isExpecting` an outcome. */
export const useAssetSheetActivity = (isExpecting = false) =>
	useQuery({
		queryKey: [...assetSheetKey, 'activity'],
		queryFn: () =>
			activityApi.listAudit({ entityType: EventEntityType.ASSET_SHEET, types: ACTIVITY_TYPES, limit: ACTIVITY_LIMIT }),
		refetchInterval: isExpecting ? ACTIVE_REFETCH_MS : IDLE_REFETCH_MS,
	})

const isSyncOutcome = (type: string) => (SYNC_OUTCOMES as string[]).includes(type)

/** A sync's outcome as its audit event records it. */
export interface LastSync {
	/** The outcome's audit event, which tells a new outcome from one already seen. */
	eventId: string
	at: string
	/** Why the run changed nothing; undefined for a run that completed. */
	failure?: string
	/** For a completed run: rows created, changed, blocked and removed. */
	counts?: Pick<AssetSheetSyncCompletedData, 'created' | 'changed' | 'blocked' | 'removed'>
	/** The tabs the run skipped, and why. */
	tabProblems: AssetSheetTabProblem[]
}

/**
 * The latest sync outcome on its own, so a run of other activity never pushes it out of reach:
 * `null` once read with none recorded, undefined until a read succeeds.
 */
export const useLastSyncQuery = (isExpecting = false) =>
	useQuery({
		queryKey: [...assetSheetKey, 'activity', 'last-sync'],
		queryFn: () => activityApi.listAudit({ entityType: EventEntityType.ASSET_SHEET, types: SYNC_OUTCOMES, limit: 1 }),
		refetchInterval: isExpecting ? ACTIVE_REFETCH_MS : IDLE_REFETCH_MS,
		select: (page) => lastSyncOf(page.data.find((e) => isSyncOutcome(e.type))),
	})

/** The latest sync outcome: `null` once read with none recorded, undefined until a read succeeds. */
export const useLastSync = (isExpecting = false): LastSync | null | undefined => useLastSyncQuery(isExpecting).data

function lastSyncOf(event: EventDTO | undefined): LastSync | null {
	if (!event) return null
	const tabProblems = Array.isArray(event.data.tabProblems) ? (event.data.tabProblems as AssetSheetTabProblem[]) : []
	if (event.type === EventType.ASSET_SHEET_SYNC_FAILED)
		return { eventId: event.eventId, at: event.timestamp, failure: String(event.data.reason), tabProblems }
	const { created, changed, blocked, removed } = event.data
	const counts = {
		created: Number(created),
		changed: Number(changed),
		blocked: Number(blocked),
		removed: Number(removed),
	}
	return { eventId: event.eventId, at: event.timestamp, counts, tabProblems }
}

/** Patch the cached progress optimistically, returning the snapshot to restore. */
function useOptimisticProgress<A>(patch: (progress: AssetSheetProgress, arg: A) => AssetSheetProgress) {
	const queryClient = useQueryClient()
	return {
		onMutate: async (arg: A) => {
			await queryClient.cancelQueries({ queryKey: progressKey })
			const previous = queryClient.getQueryData<AssetSheetProgress>(progressKey)
			if (previous) queryClient.setQueryData(progressKey, patch(previous, arg))
			return { previous }
		},
		onError: (_error: unknown, _arg: A, context: { previous?: AssetSheetProgress } | undefined) => {
			queryClient.setQueryData(progressKey, context?.previous)
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: assetSheetKey }),
	}
}

export const startSyncKey = [...assetSheetKey, 'start-sync'] as const

/** Start a sync; the page shows it running at once. */
export const useStartAssetSheetSync = () =>
	useMutation({
		mutationKey: startSyncKey,
		mutationFn: assetSheetApi.startSync,
		...useOptimisticProgress<void>((p) => ({ ...p, sync: { ...p.sync, running: true } })),
	})

/**
 * The progress with an asset's failure moved to in progress. An asset not counted as failed, such
 * as one retried from the Rows tab while its other files still download, leaves the counts alone.
 */
export function progressRetrying(p: AssetSheetProgress, assetCode: string): AssetSheetProgress {
	if (!p.downloads.failures.some((f) => f.assetCode === assetCode)) return p
	return {
		...p,
		downloads: {
			...p.downloads,
			failed: p.downloads.failed - 1,
			inProgress: p.downloads.inProgress + 1,
			failures: p.downloads.failures.filter((f) => f.assetCode !== assetCode),
		},
	}
}

/** A row with the asset's retryable files queued again, and in progress once nothing else in it has failed. */
export function rowRetrying(row: AssetSheetRowReportDTO, assetCode: string): AssetSheetRowReportDTO {
	const recorded = row.imports[assetCode]
	if (!recorded) return row
	const imports = {
		...row.imports,
		[assetCode]: {
			...recorded,
			status: VeevaVaultAssetImportStatus.IN_PROGRESS,
			files: recorded.files.map((f) =>
				f.retryable ? { ...f, status: VeevaVaultFileImportStatus.PENDING, lastError: null, retryable: false } : f,
			),
		},
	}
	const failing = row.objects.some(
		(o) => !o.removed && (o.requestError || imports[o.sourceKey]?.status === VeevaVaultAssetImportStatus.FAILED),
	)
	const derivedStatus =
		row.derivedStatus === AssetSheetDerivedRowStatus.HAS_FAILURES && !failing
			? AssetSheetDerivedRowStatus.IN_PROGRESS
			: row.derivedStatus
	return { ...row, imports, derivedStatus }
}

type RowsPage = PaginatedResponse<AssetSheetRowReportDTO>

/**
 * Retry an asset's failed files: its failure moves to in progress at once on the Overview, and
 * its files read queued in every cached page of rows; all of it is put back if the retry is refused.
 */
export const useRetryAssetSheetAsset = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: assetSheetApi.retryAsset,
		onMutate: async (assetCode: string) => {
			await Promise.all([
				queryClient.cancelQueries({ queryKey: progressKey }),
				queryClient.cancelQueries({ queryKey: rowsKey }),
			])
			const progress = queryClient.getQueryData<AssetSheetProgress>(progressKey)
			const rows = queryClient.getQueriesData<RowsPage>({ queryKey: rowsKey })
			if (progress) queryClient.setQueryData(progressKey, progressRetrying(progress, assetCode))
			queryClient.setQueriesData<RowsPage>(
				{ queryKey: rowsKey },
				(page) => page && { ...page, data: page.data.map((row) => rowRetrying(row, assetCode)) },
			)
			return { progress, rows }
		},
		onError: (_error, _assetCode, context) => {
			queryClient.setQueryData(progressKey, context?.progress)
			for (const [key, page] of context?.rows ?? []) queryClient.setQueryData(key, page)
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: assetSheetKey }),
	})
}
